import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Loader2, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Agent } from "@/types/aiToolkit";
import type { AgentHandoffSource } from "@/types/agentHandoff";
import type { AgentActionType } from "@/lib/agentActionRouter";
import type { ClickUpActionContext } from "@/hooks/useCommunicationAnalyze";
import type { AnalyzeItemInput } from "@/hooks/useCommunicationAnalyze";
import type { AgentAnalysisResult } from "@/types/communications";
import { agentAllowsChatHandoff } from "@/lib/agentChatHandoff";
import { startAgentHandoffChat } from "@/lib/agentHandoffNavigate";

interface DiscussInChatButtonProps {
  agent: Agent | null | undefined;
  source: AgentHandoffSource;
  returnPath: string;
  items: AnalyzeItemInput[];
  results: AgentAnalysisResult[];
  actionType: AgentActionType;
  clickUpContext?: ClickUpActionContext | null;
  onIndexForRag?: () => Promise<void>;
  disabled?: boolean;
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}

export function DiscussInChatButton({
  agent,
  source,
  returnPath,
  items,
  results,
  actionType,
  clickUpContext,
  onIndexForRag,
  disabled = false,
  variant = "outline",
  size = "sm",
}: DiscussInChatButtonProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  if (!agent || !agentAllowsChatHandoff(agent)) return null;

  const handleClick = async () => {
    setLoading(true);
    try {
      const path = await startAgentHandoffChat({
        agent,
        source,
        returnPath,
        items,
        results,
        actionType,
        clickUpContext: clickUpContext ?? undefined,
        onIndexForRag,
      });
      if (path) {
        setLocation(path);
      } else {
        toast({
          title: "Chat handoff unavailable",
          description: "Enable AI Chat handoff in agent settings.",
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({
        title: "Could not open chat",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      disabled={disabled || loading || results.length === 0}
      onClick={() => void handleClick()}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <MessageSquare className="h-4 w-4 mr-2" />
      )}
      Discuss in Chat
    </Button>
  );
}
